export const hasPendingCashInvoicePayment = () => {
  try {
    const parseStorageValue = (key, fallback = null) => {
      try {
        const value = localStorage.getItem(key);
        if (value === null || value === undefined) return fallback;
        const parsed = JSON.parse(value);
        return parsed === null ? fallback : parsed;
      } catch (error) {
        return fallback;
      }
    };

    // Only the invoice handed to the Payment screen can hold the lock. OrderId and
    // USOrderId are just the last order opened in a list and survive the payment,
    // so matching on them re-locks the app onto a Payment screen that cannot see
    // that invoice.
    const currentCashSales = parseStorageValue("CurrentCashSales");
    const rawIdentifiers = [currentCashSales].filter((value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string") return value.trim() !== "" && value !== "null" && value !== "undefined";
      return true;
    });
    const invoiceIdentifiers = [...new Set([
      ...rawIdentifiers,
      ...rawIdentifiers
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value)),
    ])];

    const unsyncedTransactions = JSON.parse(localStorage.getItem("UnsyncedTransactions") || "[]");
    if (!Array.isArray(unsyncedTransactions)) return false;

    const cashSalesList = unsyncedTransactions[5] || [];

    const matchesActiveInvoice = (item) => {
      const candidateNames = [
        item?.name,
        item?.Body?.name,
        item?.Body?.invoice_name,
        item?.Body?.cashInvoiceName,
        item?.Body?.cash_invoice_name,
        item?.id,
        item?.usId,
      ];

      return invoiceIdentifiers.some((identifier) => {
        const normalizedIdentifier = typeof identifier === "string" ? identifier.trim() : identifier;
        if (normalizedIdentifier === "" || normalizedIdentifier === null || normalizedIdentifier === undefined) {
          return false;
        }

        return candidateNames.some((name) => {
          if (name === null || name === undefined) return false;
          const normalizedName = typeof name === "string" ? name.trim() : name;
          return normalizedName === normalizedIdentifier || Number(normalizedName) === Number(normalizedIdentifier);
        });
      });
    };

    const incompleteCashSale = cashSalesList.find((item) => {
      if (!matchesActiveInvoice(item)) return false;

      const cashPayment = Array.isArray(item?.Body?.cashPayment) ? item.Body.cashPayment : [];
      if (cashPayment.length === 0) return true;

      return cashPayment.every((payment) => {
        const amount = Number(payment?.amount || 0);
        const hasPaymentType = Boolean(payment?.payment_type_id || payment?.payment_type);
        return amount <= 0 && !hasPaymentType;
      });
    });

    if (incompleteCashSale) return true;

    const flagValue = localStorage.getItem("PendingCashInvoicePayment");
    if (flagValue === null) return false;

    try {
      const parsedFlag = JSON.parse(flagValue);
      if (parsedFlag !== true) return false;
      return invoiceIdentifiers.length === 0;
    } catch {
      const flagIsTrue = String(flagValue).toLowerCase() === "true";
      if (!flagIsTrue) return false;
      return invoiceIdentifiers.length === 0;
    }
  } catch (error) {
    return false;
  }
};

export const hasActiveVisit = () => {
  try {
    const parseValue = (key, fallback) => {
      try {
        const value = localStorage.getItem(key);
        if (value === null || value === undefined) return fallback;
        const parsed = JSON.parse(value);
        return parsed === null ? fallback : parsed;
      } catch (error) {
        return fallback;
      }
    };

    const matchVisitRecord = (visit) => {
      if (!visit || typeof visit !== "object") return false;

      const status = String(visit.visit || visit.status || visit.state || "").toLowerCase();
      const cancelled =
        Boolean(visit.cancel) ||
        Boolean(visit.reasondisable) ||
        status === "cancel" ||
        status === "canceled" ||
        status === "cancelled";
      const checkedOut =
        Boolean(visit.disableout) ||
        Boolean(visit.dateOut) ||
        Boolean(visit.timeOut) ||
        Boolean(visit.out_time) ||
        Boolean(visit.timeVisitOut) ||
        Boolean(visit.dateVisitOut) ||
        status === "out" ||
        status === "visited";
      const hasVisitIdentity = Boolean(
        visit.id ||
          visit.client_id ||
          visit.partner_id ||
          visit.name ||
          visit.code ||
          visit.dateVisitIn ||
          visit.timeVisitIn
      );

      return !cancelled && !checkedOut && hasVisitIdentity;
    };

    const visitLists = [
      parseValue("VisitIn", []),
      parseValue("visits", []),
      parseValue("backupVisits", []),
    ];

    // Only the most recent record can be the current visit: visit-in appends and
    // visit-out closes the last entry, so older open rows are stale and must not
    // lock the app forever.
    const hasLiveVisit = visitLists.some(
      (list) => Array.isArray(list) && list.length > 0 && matchVisitRecord(list[list.length - 1])
    );
    if (hasLiveVisit) return true;

    const disableI = parseValue("disableI", false);
    return Boolean(disableI);
  } catch (error) {
    return false;
  }
};

export const findIncompleteCashSalesForSync = () => {
  try {
    const unsyncedTransactions = JSON.parse(localStorage.getItem("UnsyncedTransactions") || "[]");
    if (!Array.isArray(unsyncedTransactions) || !Array.isArray(unsyncedTransactions[5])) {
      return [];
    }

    return unsyncedTransactions[5]
      .filter((item) => {
        const cashPayment = Array.isArray(item?.Body?.cashPayment) ? item.Body.cashPayment : [];
        if (cashPayment.length === 0) return true;

        return cashPayment.every((payment) => {
          const amount = Number(payment?.amount || 0);
          const hasPaymentType = Boolean(payment?.payment_type_id || payment?.payment_type);
          return amount <= 0 && !hasPaymentType;
        });
      })
      .map((item) => {
        const name =
          item?.name ||
          item?.Body?.name ||
          item?.Body?.invoice_name ||
          item?.Body?.cashInvoiceName ||
          "Cash invoice";
        return name;
      });
  } catch (error) {
    return [];
  }
};

export const isActiveVisitRouteBlocked = (pathname) => {
  if (!pathname || typeof pathname !== "string") return false;

  const blockedTopRoutes = [
    "/",
    "/Modules",
    "/LandingPage",
    "/LandingPageRep",
    "/LandingPageWMS",
    "/LandingPageOMS",
    "/Client",
    "/List",
    "/ClientAdd",
    "/MoreInfo",
    "/EditClient",
  ];

  return blockedTopRoutes.includes(pathname);
};

export const getLoginLandingRoute = () => {
  if (hasPendingCashInvoicePayment()) {
    return "/Payment";
  }

  if (hasActiveVisit()) {
    const selectedClient = (() => {
      try {
        const value = localStorage.getItem("SelectedClient");
        if (value === null || value === undefined) return null;
        const parsed = JSON.parse(value);
        return parsed !== null && parsed !== undefined ? parsed : null;
      } catch (error) {
        return null;
      }
    })();

    return selectedClient !== null ? "/TransactionsPage" : "/Client";
  }

  return "/Modules";
};
